import svgBlockQuoteIcon from '@img/icons/blockquote-icon.svg';
import { css } from 'styled-components';
import { transition } from './animate';
import brand from './brand';
import { fluid } from './fluid';
import fonts from './fonts';
import { Device, from } from './media';
export const headingStyles = css `
  color: currentColor;
  ${fonts.zahrah.medium};
  margin: 0 0 ${fluid(16, 18)};

  @media ${from(Device.TabletLarge)} {
    letter-spacing: -0.035em;
  }
`;
export const h1 = css `
  ${headingStyles};

  font-size: ${fluid(36, 52)};
  line-height: ${fluid(42, 58)};
`;
export const h2 = css `
  ${headingStyles};
  font-size: ${fluid(32, 46)};
  line-height: ${fluid(38, 52)};
`;
export const h3 = css `
  ${headingStyles};
  font-size: ${fluid(28, 36)};
  line-height: ${fluid(34, 42)};
`;
export const h4 = css `
  ${headingStyles};
  font-size: ${fluid(24, 32)};
  line-height: ${fluid(30, 38)};
`;
export const h5 = css `
  ${headingStyles};
  font-size: ${fluid(20, 26)};
  line-height: ${fluid(26, 32)};
`;
export const h6 = css `
  ${headingStyles};
  font-size: ${fluid(18, 20)};
  line-height: ${fluid(24, 26)};
`;
const paragraphStyles = css `
  color: currentColor;
  ${fonts.brandonGrotesque.regular};
  margin: 0 0 ${fluid(14, 16)};
`;
export const paragraphLarge = css `
  ${paragraphStyles};
`;
export const paragraphMedium = css `
  ${paragraphStyles};
  font-size: ${fluid(18, 20)};
  line-height: ${fluid(24, 26)};
`;
export const paragraphSmall = css `
  ${paragraphStyles};
  font-size: ${fluid(16, 18)};
  line-height: ${fluid(22, 24)};
`;
export const paragraphMicro = css `
  ${paragraphStyles};
  font-size: 16px;
  line-height: 22px;
`;
export const tagLarge = css `
  ${paragraphStyles};
  ${fonts.brandonGrotesque.bold};
  font-size: ${fluid(18, 20)};
  line-height: ${fluid(20, 22)};
  letter-spacing: 0.01em;
  text-transform: uppercase;
`;
export const tag = css `
  ${paragraphStyles};
  ${fonts.brandonGrotesque.bold};
  font-size: ${fluid(16, 18)};
  line-height: ${fluid(18, 20)};
  letter-spacing: 0.01em;
  text-transform: uppercase;
`;
export const tagSmall = css `
  ${paragraphStyles};
  ${fonts.brandonGrotesque.bold};
  font-size: ${fluid(12, 14)};
  line-height: ${fluid(14, 16)};
  letter-spacing: 0.01em;
  text-transform: uppercase;
`;
export const link = css `
  display: inline-block;
  color: currentColor;
  background: linear-gradient(
    to right,
    var(--accentColor, ${brand.green}),
    var(--accentColor, ${brand.green})
  );
  background-repeat: no-repeat;
  background-size: 0% 100%;
  margin: 0 -4px 0;
  padding: 0 4px 0;
  text-decoration: underline;
  text-underline-offset: 3px;

  ${transition('background-size, color')};

  &:focus {
    outline: none;
  }

  &.focus-ring,
  &:focus,
  &:hover {
    background-size: 100% 100%;
    color: ${brand.white};
    text-decoration: none;
  }
`;
export const table = css `
  background: ${brand.white};
  border-collapse: collapse;
  color: ${brand.black};
  margin-bottom: 1em;
  width: 100%;

  thead {
    tr {
      background: ${brand.green};

      td,
      th {
        border: 2px solid ${brand.white};
        color: ${brand.white};
        ${fonts.zahrah.medium};
        font-size: 18px;
        letter-spacing: -0.01em;
        line-height: 22px;
        padding: 12px 16px;
        text-align: left;
      }
    }
  }

  tbody {
    tr {
      &:nth-child(odd) {
        background: ${brand.white};
      }

      &:nth-child(even) {
        background: ${brand.beige};
      }

      th {
        border: 2px solid ${brand.white};
        color: ${brand.white};
        ${fonts.zahrah.medium};
        font-size: 18px;
        letter-spacing: -0.01em;
        line-height: 22px;
        padding: 12px 16px;
        text-align: left;
      }

      td {
        border: 2px solid ${brand.white};
        ${fonts.brandonGrotesque.regular};
        font-size: 16px;
        letter-spacing: -0.01em;
        line-height: 20px;
        padding: 12px 16px;
        text-align: left;
      }
    }
  }
`;
export const blockquote = css `
  position: relative;
  ${paragraphLarge}
  ${fonts.brandonGrotesque.bold}
  padding-top: 66px;
  text-align: left;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    display: block;
    background-image: url(${svgBlockQuoteIcon});
    height: 50px;
    width: 50px;
  }
`;
export const contentStyles = css `
  h1,
  .h1-styles {
    ${h1}
  }

  h2,
  .h2-styles {
    ${h2}
  }

  h3,
  .h3-styles {
    ${h3}
  }

  h4,
  .h4-styles {
    ${h4}
  }

  h5,
  .h5-styles {
    ${h5}
  }

  p {
    ${paragraphMedium}
  }

  p.large {
    ${paragraphLarge}
  }

  p.small {
    ${paragraphSmall}
  }

  p.tag-large,
  .tag-large {
    ${tagLarge};
  }

  p.tag,
  .tag {
    ${tag};
  }

  p.tag-small,
  .tag-small {
    ${tagSmall};
  }

  p.tag,
  .tag,
  p.tag-large,
  .tag-large,
  p.tag-small,
  .tag-small {
    margin: 0 0 ${fluid(12, 14)};
  }

  a {
    ${link}
  }

  ol,
  ul {
    ${paragraphMedium};
    margin: 0 0 0.5em;

    li {
      margin: 0 0 0.33em;
    }
  }

  ol {
    list-style: decimal;
    padding-left: 1.2em;
  }

  ul {
    list-style: disc;
    padding-left: 1.2em;
  }

  hr {
    display: block;
    background: currentColor;
    border: 0;
    height: 4px;
    margin: ${fluid(40, 80)} 0;
    padding: 0;
    width: 100%;
  }

  table {
    ${table};
  }

  blockquote {
    ${blockquote};
  }

  .responsive-table {
    position: relative;
    overflow-x: auto;
  }

  > *:first-child {
    margin-top: 0;
  }

  > *:last-child {
    margin-bottom: 0;
  }
`;
